<script setup lang="ts">
import type {
  CrashAnalysisWindow,
  CrashAnalysisWindowKey,
  CrashCandleColor,
  CrashStreak,
} from '~/types/crash-pre-game';

type Props = {
  windowKeys: CrashAnalysisWindowKey[];
  window: CrashAnalysisWindow;
  /** Sequência de cor em andamento. null = o core não mandou. */
  streak: CrashStreak | null;
};

const { windowKeys, window: activeWindow, streak } = defineProps<Props>();

const selected = defineModel<CrashAnalysisWindowKey>({ required: true });

const COLOR_ORDER: CrashCandleColor[] = ['blue', 'purple', 'pink'];

// Plural na mão porque as três cores têm gêneros e formas diferentes na frase
// ("1 foi azul" / "5 foram azuis", mas "1 rosa" / "3 rosas").
const COLOR_WORDS: Record<CrashCandleColor, [string, string]> = {
  blue: ['foi azul', 'foram azuis'],
  purple: ['roxa', 'roxas'],
  pink: ['rosa', 'rosas'],
};

const STREAK_WORDS: Record<CrashCandleColor, string> = {
  blue: 'Azuis',
  purple: 'Roxas',
  pink: 'Rosas',
};

// "5min", "30min" — o rótulo da aba sai da própria chave do core.
function tabLabel(key: CrashAnalysisWindowKey): string {
  return key.replace('m', '');
}

const total = computed(() => activeWindow.total);

// Tudo aqui anda por COR, não por posição no array: `colors.blue` existe sempre
// (o mapper garante as três), enquanto um `counts[0]` é só uma aposta na ordem.
const counts = computed(() =>
  COLOR_ORDER.map((color) => ({
    color,
    count: activeWindow.colors[color].count,
  })),
);

// Largura de cada fatia: proporção real da janela. Usa o count e o total da
// MESMA janela — não o `pct`, que o core arredonda pra inteiro e faria as três
// fatias não fecharem a barra.
const slices = computed(() =>
  counts.value
    .map(({ color, count }) => ({
      color,
      count,
      width: total.value > 0 ? (count / total.value) * 100 : 0,
    }))
    .filter((slice) => slice.count > 0),
);

// "A cada 10 em média: 5 azuis, 4 roxas e 1 rosa."
//
// Maior resto: arredondar cada cor por conta própria daria 5+4+2 = 11 rodadas
// em cada 10. Aqui os três sempre somam 10 — que é a promessa da frase.
const porDez = computed<Record<CrashCandleColor, number>>(() => {
  const zeros = { blue: 0, purple: 0, pink: 0 };

  if (total.value <= 0) return zeros;

  const parts = counts.value.map(({ color, count }) => {
    const exact = (count / total.value) * 10;

    return { color, resto: exact - Math.floor(exact) };
  });

  const result = { ...zeros };

  for (const { color, count } of counts.value) {
    result[color] = Math.floor((count / total.value) * 10);
  }

  // As unidades que sobraram vão pras cores com o maior resto — é isso que faz
  // os três fecharem em 10.
  let falta = 10 - COLOR_ORDER.reduce((acc, color) => acc + result[color], 0);

  for (const { color } of [...parts].sort((a, b) => b.resto - a.resto)) {
    if (falta <= 0) break;

    result[color] += 1;
    falta -= 1;
  }

  return result;
});

const phraseParts = computed(() =>
  COLOR_ORDER.map((color) => {
    const value = porDez.value[color];
    const [one, many] = COLOR_WORDS[color];

    return { color, text: `${value} ${value === 1 ? one : many}` };
  }),
);

function formatMultiplier(multiplier: number | null): string {
  return multiplier === null ? '—' : `${multiplier.toFixed(2)}X`;
}
</script>

<template>
  <div>
    <div
      aria-label="Janela de tempo"
      class="crash-tabs"
      role="tablist"
    >
      <span class="crash-tabs__k">Nos últimos</span>

      <button
        v-for="key in windowKeys"
        :key="key"
        :aria-selected="key === selected"
        class="crash-tabs__tab"
        role="tab"
        type="button"
        @click="selected = key"
      >
        {{ tabLabel(key) }}<small>min</small>
      </button>
    </div>

    <!-- Janela sem rodada com o jogo vivo (uma pausa de 10 min deixa a aba de
         5min zerada). Barra vazia e "Foram 0 rodadas" não informam nada; a
         linha diz o que houve e as abas seguem ali pra trocar de janela. -->
    <p
      v-if="total === 0"
      class="crash-empty"
    >
      Nenhuma rodada nesta janela de tempo.
    </p>

    <!-- `key` na janela escolhida: trocar de aba REMONTA o conteúdo, e é isso
         que faz a barra crescer de novo em vez de pular pro novo tamanho (a
         atualização do polling não passa por aqui — só a troca de aba muda a
         chave).
         Quem anima é só a barra. Um fade com deslocamento no bloco inteiro
         parecia defeito: o texto piscava e as barrinhas subiam junto, como se a
         altura tivesse pulado. -->
    <div
      v-else
      :key="selected"
      class="crash-window"
    >
      <!-- Barra de proporção: a contagem vai DENTRO da fatia, porque é ali que
           ela quer dizer alguma coisa. -->
      <div class="crash-prop">
        <i
          v-for="(slice, index) in slices"
          :key="slice.color"
          class="crash-prop__slice"
          :class="`crash-prop__slice--${slice.color}`"
          :style="{
            width: `${slice.width}%`,
            animationDelay: `${index * 70}ms`,
          }"
        >
          {{ slice.count }}
        </i>
      </div>

      <p class="crash-phrase">
        Foram <b>{{ total }} {{ total === 1 ? 'rodada' : 'rodadas' }}</b
        >. A cada 10 em média:
        <template
          v-for="(part, index) in phraseParts"
          :key="part.color"
        >
          <span :class="`crash-phrase--${part.color}`">{{ part.text }}</span
          ><template v-if="index === 0">, </template
          ><template v-else-if="index === 1"> e </template
          ><template v-else>.</template>
        </template>
      </p>

      <div class="crash-stats">
        <div class="crash-stat">
          <p class="crash-stat__k">A maior de todas</p>
          <p class="crash-stat__val crash-stat__val--pink">
            {{ formatMultiplier(activeWindow.biggest) }}
          </p>
        </div>

        <div class="crash-stat">
          <p class="crash-stat__k">Média das rodadas</p>
          <p class="crash-stat__val">
            {{ formatMultiplier(activeWindow.avgMultiplier) }}
          </p>
        </div>

        <!-- A sequência é do jogo inteiro, não da janela: some quando o core não
           manda, em vez de mostrar um zero que pareceria "acabou agora". -->
        <div
          v-if="streak"
          class="crash-stat"
        >
          <p class="crash-stat__k">
            {{ STREAK_WORDS[streak.color] }} seguidas agora
          </p>
          <p
            class="crash-stat__val"
            :class="`crash-stat__val--${streak.color}`"
          >
            {{ streak.length }}
          </p>
        </div>

        <div class="crash-stat">
          <p class="crash-stat__k">Rosas que saíram</p>
          <p class="crash-stat__val">{{ activeWindow.colors.pink.count }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.crash-empty {
  margin: 0;
  padding: 14px 0 2px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
}

.crash-tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 14px;
  margin: 0 0 16px;
}

@media (min-width: 1024px) {
  .crash-tabs {
    gap: 22px;
    margin-bottom: 18px;
  }
}

.crash-tabs__k {
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
}

.crash-tabs__tab {
  padding: 6px 0;
  border: 0;
  border-bottom: 2px solid transparent;
  background: none;
  color: rgba(255, 255, 255, 0.9);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
}

.crash-tabs__tab[aria-selected='true'] {
  color: #e9eef9;
  border-bottom-color: #e9eef9;
}

.crash-tabs__tab small {
  margin-left: 2px;
  font-size: 11px;
  font-weight: 600;
}

.crash-prop {
  display: flex;
  gap: 3px;
  height: 34px;
  border-radius: 12px;
  background: #161f35;
  overflow: hidden;
}

@media (min-width: 1024px) {
  .crash-prop {
    height: 36px;
  }
}

.crash-prop__slice {
  display: flex;
  align-items: center;
  /* Centralizado no mobile: encostado à esquerda, o número da fatia estreita
     (a rosa costuma ficar em ~10% da barra) fica espremido no canto e chega a
     não caber. O padding de 4px é só pra ele não colar na emenda. */
  justify-content: center;
  padding: 0 4px;
  /* Piso pra caber o número. Quando a fatia menor esbarra aqui, ela para de
     encolher e o flex tira o que falta das outras — proporcional ao tamanho de
     cada uma, que é o que mantém a leitura de proporção de pé. Como é em px e
     não em %, só entra em cena quando a barra é estreita: no desktop, 4% já dá
     mais que isso e nenhuma fatia é distorcida. */
  min-width: 34px;
  border-radius: 12px;
  overflow: hidden;
  color: #070b14;
  font-size: 13px;
  font-weight: 800;
  font-style: normal;
  font-variant-numeric: tabular-nums;
  /* A largura vem do dado; a animação só faz a fatia crescer na entrada. Tem
     que ser @keyframes: o main.scss aplica `transition: none !important` no app
     inteiro, então animar `width` por transition não funcionaria. */
  transform-origin: left center;
  animation: crash-prop-in 800ms cubic-bezier(0.22, 0.9, 0.3, 1) both;
}

/* No desktop a barra é larga o bastante pra cada fatia ter folga, e aí o número
   alinhado à esquerda — como no layout de origem — dá o ponto de leitura certo:
   todos começam na mesma borda que a fatia. */
@media (min-width: 1024px) {
  .crash-prop__slice {
    justify-content: flex-start;
    padding: 0 12px;
  }
}

.crash-prop__slice--blue {
  background: #37c6f0;
}

.crash-prop__slice--purple {
  background: #a560f5;
}

.crash-prop__slice--pink {
  background: #ec5a98;
}

.crash-phrase {
  margin: 14px 0 0;
  color: #e9eef9;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  /* Sem teto de largura: a frase é uma linha só e usa o bloco inteiro. O
     `max-width: 52ch` que existia aqui no desktop é que a fazia quebrar, com
     meio card vazio à direita. */
  text-wrap: pretty;
}

/* Número e cor são uma unidade de leitura: "1" numa linha e "rosa" na seguinte
   quebra a conta no meio. Cada trecho colorido (e a contagem total) anda
   inteiro pra próxima linha ou não anda. */
.crash-phrase b,
.crash-phrase--blue,
.crash-phrase--purple,
.crash-phrase--pink {
  white-space: nowrap;
}

.crash-phrase--blue {
  color: #37c6f0;
}

.crash-phrase--purple {
  color: #8f5be6;
}

.crash-phrase--pink {
  color: #ff8fbe;
}

.crash-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin: 22px 0 0;
  border-top: 1px solid #1c2740;
}

@media (min-width: 1024px) {
  .crash-stats {
    grid-template-columns: repeat(4, 1fr);
    margin-top: 26px;
  }
}

.crash-stat {
  padding: 13px 14px 0 0;
}

.crash-stat:nth-child(even) {
  padding-right: 0;
  padding-left: 14px;
  border-left: 1px solid #1c2740;
}

.crash-stat:nth-child(n + 3) {
  margin-top: 13px;
  padding-top: 13px;
  border-top: 1px solid #1c2740;
}

@media (min-width: 1024px) {
  .crash-stat {
    padding: 14px 18px 0 0;
  }

  .crash-stat + .crash-stat {
    padding-left: 18px;
    border-left: 1px solid #1c2740;
  }

  .crash-stat:nth-child(n + 3) {
    margin-top: 0;
    border-top: 0;
  }
}

.crash-stat__k {
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
}

.crash-stat__val {
  margin: 5px 0 0;
  color: #e9eef9;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
}

.crash-stat__val--blue {
  color: #37c6f0;
}

.crash-stat__val--purple {
  color: #8f5be6;
}

.crash-stat__val--pink {
  color: #ff8fbe;
}

@keyframes crash-prop-in {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .crash-prop__slice {
    animation: none;
  }
}
</style>
